import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { tv } from 'tailwind-variants'
import { ITableViewWithColumns } from 'types'
import { AllViews, Dots, Plus } from 'components/icons'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { useAuth } from 'hooks/auth'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { groupViews, useSelectView } from 'components/spreadsheet/hooks/useViewSwitching'
import { DEFAULT_VIEW_ROW_PRESENTATION, VIEW_ROW_PRESENTATION } from 'components/spreadsheet/components/menu/views/view'
import { pageHeaderButton } from 'components/pageHeader/styles'
import { rowButtonProps } from 'components/sidebar/rowButton'
import Spinner from 'components/spinner'
import { useCustomViewPanel } from 'components/spreadsheet/components/custom-view/panel'

const { action } = pageHeaderButton()

// The gap between tabs (gap-0.5).
const TAB_GAP = 2

// How the row moves when the view changes: the tabs glide to their new places,
// a tab joining the row fades in, and the open tab's shading slides across.
const MOVE_MS = 220
const ENTER_MS = 180
const EASE = 'cubic-bezier(0.2, 0, 0, 1)'
const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// The least room kept for the tabs, about one short tab's worth.
const MIN_TAB_WIDTH = 96
// The most a tab takes, so one long name can't crowd out the others: past it the
// name truncates, in full in its Tooltip and in All views.
export const MAX_TAB_WIDTH = 240

// On top of the page header's labelled action (pageHeaderButton's action): a rounded rectangle
// like the header's other actions. The open view's is shaded, with its options button (⋯) inside
// it, at its end: the item, not the tab, carries the shading, so it widens to hold both.
const viewTabStyles = tv({
  slots: {
    // Positioned, so the open tab's shading (drawn under the tabs, so it can slide) sits behind it.
    item: 'relative flex items-center shrink-0 min-w-0 rounded',
    // Shrinks (the Button is shrink-0) so a tab cut short truncates its name
    // instead of spilling over the next.
    tab: 'min-w-0 shrink',
    // The slim ⋮ in a square of its own, padded evenly round the icon, with
    // nothing drawn: inset in the tab by 4px all round, right after the name
    // (its own padding is the gap).
    options: [
      'flex items-center justify-center shrink-0 size-5 mr-1 rounded cursor-pointer',
      'text-muted hover:text-body [&_svg]:w-3.5 [&_svg]:h-3.5'
    ],
    control: 'shrink-0 text-muted hover:text-body'
  },
  variants: {
    open: {
      true: {
        tab: 'pr-0 text-body font-semibold hover:bg-transparent'
      },
      false: {
        tab: 'text-muted hover:text-body'
      }
    },
    personal: {
      true: { tab: 'italic' }
    }
  }
})

const { control } = viewTabStyles()

// The views to show as tabs: every top-level view, each followed by its
// personal views that are yours. Which view is open doesn't change the list.
const tabViews = (
  parentViews: ITableViewWithColumns[],
  personalViewsByParent: Record<string, ITableViewWithColumns[]>,
  userId: string | undefined
): ITableViewWithColumns[] => {
  const isMine = (child: ITableViewWithColumns): boolean =>
    userId !== undefined && child.personalViewOwner?.publicId === userId
  return parentViews.flatMap((view) => [view, ...(personalViewsByParent[view.publicId] || []).filter(isMine)])
}

interface ViewTabsProps {
  /** Shown in place of the renamed view's tab while a view is being renamed. */
  renameInput?: React.ReactNode
  /** The view being renamed (the open one's, unless renamed from another tab's menu). */
  renamingViewId?: string | null
  /** Double-clicking the current view's tab starts renaming it. */
  onRenameCurrent: () => void
  /** Opens the New … View choices, anchored to the button. */
  onNewView: (event: React.MouseEvent<HTMLElement>) => void
  /** Opens the full view menu: every view, searchable, with renaming and deleting. */
  onAllViews: (event: React.MouseEvent<HTMLElement>) => void
  /** Opens the view menu from the open view's tab, led by that view's own settings. */
  onCurrentView: (event: React.MouseEvent<HTMLElement>) => void
  /**
   * Opens the view menu from any view's tab, led by that view's own settings:
   * on right-click, or from the tab's options button.
   */
  onViewMenu: (event: React.SyntheticEvent<HTMLElement>, viewId: string) => void
}

// What the tab row measures: the room it has, and how wide each part is in
// full. Tab widths include the gap after them.
interface TabMetrics {
  available: number
  tabWidths: Record<string, number>
  // All views, before the tabs.
  views: number
  // "N more", after them when some views have no tab.
  more: number
  // + New view, last.
  plus: number
}

const total = (widths: number[]): number => widths.reduce((sum, width) => sum + width, 0)

// How many of these tabs fit in room, in order, each in full.
const fitCount = (widths: number[], room: number): number => {
  let used = 0
  let count = 0
  for (const width of widths) {
    used += width
    if (used > room) break
    count += 1
  }
  return count
}

// The room for the tabs: between All views and New view, and beside "N more"
// when some views have no tab. Each tab's measured width includes the gap after
// it, but the last one's is the gap before what follows, counted here already,
// so it's given back.
const roomForTabs = (metrics: TabMetrics, withMore: boolean): number =>
  metrics.available - metrics.views - metrics.plus - TAB_GAP - (withMore ? metrics.more + TAB_GAP : 0)

// How many of the listed views get a tab (null: every one). Tabs are never cut
// short: every one, if they all fit in full; otherwise as many as fit in full
// beside "N more", and at least one, for the open view. That count depends only
// on the room and the listed views, never on which view is open.
const layoutTabs = (metrics: TabMetrics, listedWidths: number[], unlistedWidth: number | undefined): number | null => {
  const all = unlistedWidth === undefined ? listedWidths : [...listedWidths, unlistedWidth]
  if (total(all) <= roomForTabs(metrics, false)) return null
  return Math.max(1, fitCount(listedWidths, roomForTabs(metrics, true)))
}

/**
 * The table page's views, side by side in the order they were made: click one
 * to open it. The views are the ones the view menu lists, with personal views
 * shown only when they're yours. As many as fit in full are shown, never cut
 * short; the rest are counted in "N more", which opens the full view menu as All
 * views, first in the row, does (every view, searchable, with renaming and deleting). Clicking
 * the open view's tab opens it too, led by that view's own settings.
 *
 * Given the room, which tabs show doesn't depend on which view is open, but for
 * the last: the open view always has a tab, and when it isn't one of those that
 * fit, it takes the last one's place. They have at least half the view row, and
 * whatever more the open view's actions leave. New view is always there, so
 * it's clear views can be added.
 */
const ViewTabs: React.FC<ViewTabsProps> = ({
  renameInput,
  renamingViewId,
  onRenameCurrent,
  onNewView,
  onAllViews,
  onCurrentView,
  onViewMenu
}) => {
  const { spreadsheetData, inProcess, originalViewId } = useDataContext()
  // While MortaAI works on the open Custom View, its tab shows a spinner.
  const customViewWorking = useCustomViewPanel().working
  const { user } = useAuth()
  const { selectView, hydratingViewId } = useSelectView()
  const currentId = spreadsheetData.viewDetails.publicId

  const { parentViews, personalViewsByParent } = groupViews(spreadsheetData.tableView, inProcess, originalViewId)
  const listedViews = tabViews(parentViews, personalViewsByParent, user?.publicId)
  // The open view, when it isn't one of the listed ones (someone else's
  // personal view). It still gets a tab, after the others.
  const unlistedCurrent = listedViews.some((view) => view.publicId === currentId)
    ? undefined
    : spreadsheetData.tableView.find((view) => view.publicId === currentId)

  // Every tab is measured once in full, in an invisible copy of the row, along
  // with All views and New view; the room is the row's own width.
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  // Null until laid out (or with no layout at all, as in tests): every tab shows.
  const [metrics, setMetrics] = useState<TabMetrics | null>(null)

  const measure = () => {
    const container = containerRef.current
    const measured = measureRef.current
    if (!container || !measured) return
    const widthOf = (selector: string) => measured.querySelector<HTMLElement>(selector)?.offsetWidth ?? 0
    const widthsOf = (selector: string) =>
      Object.fromEntries(
        Array.from(measured.querySelectorAll<HTMLElement>(selector)).map((tab) => [
          tab.dataset.measureId,
          tab.offsetWidth + TAB_GAP
        ])
      )
    const next =
      container.clientWidth === 0
        ? null
        : {
            available: container.clientWidth,
            tabWidths: widthsOf('[data-measure="tab"]'),
            views: widthOf('[data-measure="views"]'),
            more: widthOf('[data-measure="more"]'),
            plus: widthOf('[data-measure="new"]')
          }
    if (JSON.stringify(next) !== JSON.stringify(metrics)) setMetrics(next)
  }

  useLayoutEffect(measure)
  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(container)
    return () => observer.disconnect()
  })

  // Tabs are measured in full and laid out at no more than MAX_TAB_WIDTH (each
  // measured width includes the gap after it).
  const fullWidthOfTab = (view: ITableViewWithColumns): number => metrics?.tabWidths[view.publicId] ?? 0
  const widthOfTab = (view: ITableViewWithColumns): number => Math.min(fullWidthOfTab(view), MAX_TAB_WIDTH + TAB_GAP)
  const places = metrics
    ? layoutTabs(metrics, listedViews.map(widthOfTab), unlistedCurrent && widthOfTab(unlistedCurrent))
    : null

  // The tabs to show: the first ones that fit, whichever view is open. When
  // the open view isn't one of them, it takes the last one's place (or, when
  // every tab fits, someone else's personal view joins them at the end).
  const fittingViews = places === null ? listedViews : listedViews.slice(0, places)
  const currentView = unlistedCurrent ?? listedViews.find((view) => view.publicId === currentId)
  const inLastPlace = currentView && !fittingViews.includes(currentView) ? currentView : undefined
  const tabsToShow = (): ITableViewWithColumns[] => {
    if (!inLastPlace) return fittingViews
    if (places === null || !metrics) return [...fittingViews, inLastPlace]
    // The open view takes the last place, and as many before it as still fit
    // in full beside it keep theirs.
    const before = fittingViews.slice(0, -1)
    const room = roomForTabs(metrics, true) - widthOfTab(inLastPlace)
    return [...before.slice(0, fitCount(before.map(widthOfTab), room)), inLastPlace]
  }
  const shownViews = tabsToShow()
  const hiddenCount = listedViews.length + (unlistedCurrent ? 1 : 0) - shownViews.length

  // Where each part of the row was last laid out (its offsetLeft, which a running
  // animation's transform doesn't change), keyed by data-flip-key.
  const positions = useRef(new Map<string, number>())
  const highlightRef = useRef<HTMLDivElement>(null)
  // When the open view, or which tabs show, changes, each part that moved glides
  // from where it was, and each new one fades in. (Laid out afresh for other
  // reasons, such as the window resizing, nothing animates.)
  const rowShape = `${currentId}|${shownViews.map((view) => view.publicId).join(',')}|${hiddenCount}`
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container || positions.current.size === 0 || prefersReducedMotion()) return
    container.querySelectorAll<HTMLElement>('[data-flip-key]').forEach((element) => {
      if (typeof element.animate !== 'function') return
      const before = positions.current.get(element.dataset.flipKey as string)
      if (before === undefined) {
        element.animate(
          [
            { opacity: 0, transform: 'translateX(-6px)' },
            { opacity: 1, transform: 'none' }
          ],
          {
            duration: ENTER_MS,
            easing: EASE
          }
        )
      } else if (before !== element.offsetLeft) {
        element.animate([{ transform: `translateX(${before - element.offsetLeft}px)` }, { transform: 'none' }], {
          duration: MOVE_MS,
          easing: EASE
        })
      }
    })
  }, [rowShape])
  // After every layout: remember where everything is, and put the shading under
  // the open tab (sliding there, once it has been placed the first time).
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const next = new Map<string, number>()
    container.querySelectorAll<HTMLElement>('[data-flip-key]').forEach((element) => {
      next.set(element.dataset.flipKey as string, element.offsetLeft)
    })
    positions.current = next

    const highlight = highlightRef.current
    if (!highlight) return
    const open = container.querySelector<HTMLElement>('[data-open-tab]')
    if (!open) {
      highlight.style.opacity = '0'
      return
    }
    highlight.style.opacity = '1'
    highlight.style.transform = `translate(${open.offsetLeft}px, ${open.offsetTop}px)`
    highlight.style.width = `${open.offsetWidth}px`
    highlight.style.height = `${open.offsetHeight}px`
    if (!highlight.dataset.placed && !prefersReducedMotion()) {
      // Placed without a slide; from then on it slides.
      requestAnimationFrame(() => {
        highlight.dataset.placed = 'true'
      })
    }
  })
  // A name too long for the tabs' whole room on its own is the one cut short,
  // to that room, rather than spill past New view.
  const tabRoom = metrics ? roomForTabs(metrics, hiddenCount > 0) : 0
  const tooWide = (view: ITableViewWithColumns): boolean => metrics !== null && widthOfTab(view) > tabRoom

  // The least room the tabs take: a short tab, All views, "N more" and New
  // view. The breadcrumb shrinks before they do.
  const minWidth = metrics ? MIN_TAB_WIDTH + metrics.views + metrics.more + metrics.plus + TAB_GAP * 4 : 160

  // A view's tab, or (measuring) its invisible copy. Every tab is measured as if
  // open, with its options button, the widest a tab gets, so how many fit
  // doesn't depend on which view is open.
  const tab = (view: ITableViewWithColumns, measuring = false) => {
    const isCurrent = view.publicId === currentId
    if (!measuring && view.publicId === renamingViewId && renameInput)
      return <React.Fragment key={view.publicId}>{renameInput}</React.Fragment>
    const { icon } = VIEW_ROW_PRESENTATION[view.type] ?? DEFAULT_VIEW_ROW_PRESENTATION
    const open = isCurrent || measuring
    const styles = viewTabStyles({ open, personal: view.isPersonalView })
    // A tab is cut to the tabs' whole room when even that's less, or else to MAX_TAB_WIDTH.
    const cutTo = (): number | undefined => {
      if (measuring) return undefined
      if (tooWide(view)) return tabRoom - TAB_GAP
      return fullWidthOfTab(view) > MAX_TAB_WIDTH + TAB_GAP ? MAX_TAB_WIDTH : undefined
    }
    const cut = cutTo()
    const button = (
      <Button
        internalType="transparent"
        tabIndex={measuring ? -1 : undefined}
        {...(measuring
          ? {}
          : {
              role: 'tab',
              'aria-selected': isCurrent,
              'data-testid': isCurrent ? 'table-name-select' : 'view-tab',
              'data-view-id': view.publicId
            })}
        disabled={hydratingViewId !== null}
        className={action({ class: styles.tab() })}
        onClick={(event: React.MouseEvent<HTMLElement>) => {
          if (measuring) return
          // The open view's tab opens the view menu, led by the view's own
          // settings, and with every view below so they're within reach even
          // when every tab fits.
          if (isCurrent) onCurrentView(event)
          else selectView(view.publicId)
        }}
        onDoubleClick={() => {
          if (!measuring && isCurrent) onRenameCurrent()
        }}
        // Right-clicking any tab opens the view menu as clicking the open
        // view's does, led by that tab's view, without opening it.
        onContextMenu={(event: React.MouseEvent<HTMLElement>) => {
          if (measuring) return
          event.preventDefault()
          onViewMenu(event, view.publicId)
        }}
      >
        {icon}
        <span className="truncate">{view.name}</span>
        {isCurrent && !measuring && customViewWorking && (
          <Spinner size="small" className="w-3 h-3 border-2" aria-label="MortaAI is working on this view" />
        )}
      </Button>
    )
    return (
      <div
        key={view.publicId}
        className={styles.item()}
        style={cut === undefined ? undefined : { maxWidth: cut }}
        {...(measuring
          ? { 'data-measure': 'tab', 'data-measure-id': view.publicId }
          : { 'data-flip-key': view.publicId, ...(isCurrent ? { 'data-open-tab': true } : {}) })}
      >
        {cut === undefined ? button : <Tooltip content={view.name}>{button}</Tooltip>}
        {/* Only the open view's tab has its options button, always there. */}
        {open && (
          <div
            {...(measuring
              ? { 'aria-hidden': true }
              : {
                  'data-testid': 'view-tab-options-button',
                  'aria-label': `${view.name} options`,
                  'aria-haspopup': 'menu',
                  ...rowButtonProps((event) => onViewMenu(event, view.publicId))
                })}
            className={styles.options()}
          >
            <Dots />
          </div>
        )}
      </div>
    )
  }

  // All views, first in the row: the full view menu (every view, searchable,
  // and the New … View choices), whichever views have a tab. Just an icon, a
  // menu's lines, so it doesn't read as one of the tabs; named by its tooltip.
  const allViewsButton = (measuring = false) => {
    const button = (
      <Button
        internalType="transparent"
        tabIndex={measuring ? -1 : undefined}
        aria-label="All views"
        {...(measuring
          ? { 'data-measure': 'views' }
          : { 'data-testid': 'table-views-menu-button', 'data-flip-key': 'all-views', 'aria-haspopup': 'menu' })}
        className={action({ class: control() })}
        onClick={measuring ? undefined : onAllViews}
      >
        <AllViews />
      </Button>
    )
    return measuring ? button : <Tooltip content="All views">{button}</Tooltip>
  }

  // "N more", after the tabs when some views have no tab: opens the same menu
  // as All views. Measured at the most it can count: every view but the open
  // one, which always has a tab.
  const moreButton = (count: number, measuring = false) => (
    <Button
      internalType="transparent"
      tabIndex={measuring ? -1 : undefined}
      {...(measuring
        ? { 'data-measure': 'more' }
        : { 'data-testid': 'table-views-more-button', 'data-flip-key': 'more', 'aria-haspopup': 'menu' })}
      className={action({ class: control() })}
      onClick={measuring ? undefined : onAllViews}
    >
      {count} more
    </Button>
  )

  // + New view, last in the row.
  const newViewButton = (measuring = false) => (
    <Button
      internalType="transparent"
      tabIndex={measuring ? -1 : undefined}
      // The Create a view tour opens the New … View choices here.
      {...(measuring
        ? { 'data-measure': 'new' }
        : { 'data-testid': 'table-new-view-button', 'data-tour': 'view-selector', 'data-flip-key': 'new' })}
      className={action({ class: control() })}
      onClick={measuring ? undefined : onNewView}
    >
      <Plus />
      New view
    </Button>
  )

  return (
    // All views, the tabs, "N more" and New view sit together with one small gap.
    <div ref={containerRef} className="relative flex items-center gap-0.5 flex-1 overflow-hidden" style={{ minWidth }}>
      {allViewsButton()}
      <div className="relative flex items-center gap-0.5 min-w-0 overflow-hidden" role="tablist" aria-label="Views">
        {/* The open tab's shading, under the tabs, so it can slide from one to the next. */}
        <div
          ref={highlightRef}
          aria-hidden
          data-testid="view-tab-highlight"
          className="absolute left-0 top-0 rounded bg-surface-sunken pointer-events-none opacity-0 data-[placed]:transition-[transform,width,height] data-[placed]:duration-[220ms] data-[placed]:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none"
        />
        {shownViews.map((view) => tab(view))}
      </div>
      {hiddenCount > 0 && moreButton(hiddenCount)}
      {newViewButton()}

      {/* An invisible copy of every tab, measured to decide how many fit. */}
      <div
        ref={measureRef}
        aria-hidden
        className="absolute left-0 top-0 flex items-center gap-0.5 invisible pointer-events-none whitespace-nowrap"
      >
        {listedViews.map((view) => tab(view, true))}
        {unlistedCurrent && tab(unlistedCurrent, true)}
        {allViewsButton(true)}
        {moreButton(Math.max(1, listedViews.length + (unlistedCurrent ? 1 : 0) - 1), true)}
        {newViewButton(true)}
      </div>
    </div>
  )
}

export default ViewTabs
